import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useEffectivePermissions } from './hooks/useEffectivePermissions';
import { TourProvider } from './context/TourContext';
import { OrgProvider } from './context/OrgContext';
import { NotificationProvider } from './context/NotificationContext';
import { AIGenerationProvider } from './context/AIGenerationContext';
import { JobsProvider } from './context/JobsContext';
import GlobalJobsPill from './components/GlobalJobsPill';
import ActionDialogQueue from './components/ActionDialogQueue';
import GenerationToastBridge from './components/GenerationToastBridge';
import { CrashGuard } from './components/CrashGuard';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import GamificationToastContainer from './components/gamification/XPToast';
import SurpriseRewardContainer from './components/gamification/SurpriseRewardModal';
import SlugRedirect from './components/routing/SlugRedirect';

// ─── Eagerly loaded (critical path: login + courses grid) ───
import LoginPage from './pages/LoginPage';
import CoursesPage from './pages/CoursesPage';

// ─── Lazy loaded (everything else) ───
// Self-recovering lazy loader: if a chunk was fetched during a deploy/hot-reload
// window (stale chunk → module resolves without a default export, or a
// ChunkLoadError), force ONE full reload to pick up the fresh bundle instead of
// wedging the route on "Lazy element type must resolve to a class or function".
const lazyWithReload = (importer) =>
  React.lazy(() =>
    importer()
      .then((m) => {
        if (!m || !m.default) throw new Error('Lazy module resolved without a default export');
        try { window.sessionStorage.removeItem('lazy-chunk-reloaded'); } catch (_e) { /* ignore */ }
        return m;
      })
      .catch((err) => {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = window.sessionStorage.getItem('lazy-chunk-reloaded') === '1';
          if (!alreadyReloaded) window.sessionStorage.setItem('lazy-chunk-reloaded', '1');
        } catch (_e) { /* storage unavailable — fall through to throw */ alreadyReloaded = true; }
        if (!alreadyReloaded) {
          window.location.reload();
          return new Promise(() => {}); // page is reloading; never resolve
        }
        throw err;
      })
  );
const RegisterPage = lazyWithReload(() => import('./pages/RegisterPage'));
const SelfOnboardPage = lazyWithReload(() => import('./pages/SelfOnboardPage'));
const ForgotPasswordPage = lazyWithReload(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithReload(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'));
const OrganizationSettingsPage = lazyWithReload(() => import('./pages/OrganizationSettingsPage'));
const SuperAdminDashboard = lazyWithReload(() => import('./pages/SuperAdminDashboard'));
const IndustryLabRegistryPage = lazyWithReload(() => import('./pages/superadmin/IndustryLabRegistryPage'));
const Lens1GalleryPage = lazyWithReload(() => import('./pages/Lens1GalleryPage'));
const WorksheetGalleryPage = lazyWithReload(() => import('./pages/WorksheetGalleryPage'));
const UserManagementPage = lazyWithReload(() => import('./pages/UserManagementPage'));
const CohortDetailPage = lazyWithReload(() => import('./pages/cohort-detail/CohortDetailPage'));
const CourseBuilderPage = lazyWithReload(() => import('./pages/CourseBuilderPage'));
const CourseViewPage = lazyWithReload(() => import('./pages/CourseViewPage'));

// Tiny wrappers that force the page to fully remount when `:courseId` changes
// (e.g. when a stage-completion modal deep-links from Course A → Course B).
// Without `key`, React Router reuses the same component instance which keeps
// Course A's topics / blocks / active state visible while Course B loads.
function CourseBuilderRoute() {
  const { courseId } = useParams();
  return <CourseBuilderPage key={courseId} />;
}
function CourseViewRoute() {
  const { courseId } = useParams();
  return <CourseViewPage key={courseId} />;
}
const CourseContentPage = lazyWithReload(() => import('./pages/CourseContentPage'));
const EnhancedAnalyticsPage = lazyWithReload(() => import('./pages/EnhancedAnalyticsPage'));
const CourseAuthoringWizard = lazyWithReload(() => import('./pages/Courses/CourseAuthoringWizard'));
const ContentWizardPage = lazyWithReload(() => import('./pages/Courses/ContentWizardPage'));
const LabBuilderPage = lazyWithReload(() => import('./pages/LabBuilderPage'));
const LabResultsPage = lazyWithReload(() => import('./pages/LabResultsPage'));
const EPMResultsPage = lazyWithReload(() => import('./pages/EPMResultsPage'));
const LabSubmissionsPage = lazyWithReload(() => import('./pages/LabSubmissionsPage'));
const QuizSubmissionsPage = lazyWithReload(() => import('./pages/QuizSubmissionsPage'));
const InstructorDashboardPage = lazyWithReload(() => import('./pages/InstructorDashboardPage'));
const ReviewTasksPage = lazyWithReload(() => import('./pages/ReviewTasksPage'));
const LabsPage = lazyWithReload(() => import('./pages/LabsPage'));
const GuardDigestPage = lazyWithReload(() => import('./pages/GuardDigestPage'));
const MisconceptionDashboardPage = lazyWithReload(() => import('./pages/MisconceptionDashboardPage'));
const HelpPage = lazyWithReload(() => import('./pages/HelpPage'));
const CommunityPage = lazyWithReload(() => import('./pages/CommunityPage'));
const CommunityLandingPage = lazyWithReload(() => import('./pages/CommunityLandingPage'));
const DiscussionDetailPage = lazyWithReload(() => import('./pages/DiscussionDetailPage'));
const NotificationsPage = lazyWithReload(() => import('./pages/NotificationsPage'));
const NotificationSettingsPage = lazyWithReload(() => import('./pages/NotificationSettingsPage'));
const AdminConsolePage = lazyWithReload(() => import('./pages/AdminConsolePage'));
const SupportTicketPage = lazyWithReload(() => import('./pages/SupportTicketPage'));
const CreateTicketPage = lazyWithReload(() => import('./pages/CreateTicketPage'));
const MyCertificatesPage = lazyWithReload(() => import('./pages/MyCertificatesPage'));
const DocumentationPage = lazyWithReload(() => import('./pages/DocumentationPage'));
const MyAnalyticsPage = lazyWithReload(() => import('./pages/MyAnalyticsPage'));
const GamificationAdminPage = lazyWithReload(() => import('./pages/GamificationAdminPage'));
const VideoPipelineSettingsPage = lazyWithReload(() => import('./pages/VideoPipelineSettingsPage'));
const AIApiCallsPage = lazyWithReload(() => import('./pages/AIApiCallsPage'));
const SuperAdminLeanCanvasAIPage = lazyWithReload(() => import('./pages/SuperAdminLeanCanvasAIPage'));
const SuperAdminStakeholderSimPage = lazyWithReload(() => import('./pages/SuperAdminStakeholderSimPage'));
const AnnouncementComposerPage = lazyWithReload(() => import('./pages/admin/AnnouncementComposerPage'));
const AnnouncementHistoryPage = lazyWithReload(() => import('./pages/admin/AnnouncementHistoryPage'));
const ReviewCoveragePage = lazyWithReload(() => import('./pages/admin/ReviewCoveragePage'));
const SystemAnnouncementPage = lazyWithReload(() => import('./pages/superadmin/SystemAnnouncementPage'));
const JobMonitorPage = lazyWithReload(() => import('./pages/superadmin/JobMonitorPage'));
const DiagnosticAdminPage = lazyWithReload(() => import('./pages/superadmin/DiagnosticAdminPage'));
const DiagnosticHomePage = lazyWithReload(() => import('./diagnostic/pages/DiagnosticHomePage'));
const DiagnosticInvitePage = lazyWithReload(() => import('./diagnostic/pages/DiagnosticInvitePage'));
const DiagnosticStagePage = lazyWithReload(() => import('./diagnostic/pages/SurveyRunnerPage'));
const DiagnosticReportPage = lazyWithReload(() => import('./diagnostic/pages/UsageReportPage'));
const PerfDashboardPage = lazyWithReload(() => import('./pages/superadmin/PerfDashboardPage'));
// 2026-02-24 — User-scoped background jobs history. Sibling to
// JobMonitorPage but limited to the caller's own jobs.
const JobsHistoryPage = lazyWithReload(() => import('./pages/JobsHistoryPage'));
// [Industry Lab v1-v4 pages archived — see /app/_archive/industry-lab-legacy]
// Single "being rebuilt" placeholder for all /industry-labs/* paths.
const IndustryLabsArchivedPage = lazyWithReload(() => import('./pages/IndustryLabsArchivedPage'));
const IndustryLabsLibraryPage = lazyWithReload(() => import('./pages/IndustryLabsLibraryPage'));
const IndustryLabPlayPage = lazyWithReload(() => import('./pages/IndustryLabsLibraryPage').then(m => ({ default: m.IndustryLabPlayPage })));

const AuthCallback = lazyWithReload(() => import('./pages/AuthCallback'));

// EmbedShell — wraps a page so it renders in "bare" mode (no platform
// sidebar / topbar). Used by /embed/* routes loaded inside the Immersive
// Content Player iframe.
import { EmbedShell } from './context/EmbedContext';

// Loading spinner component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected Route Component (single-domain mode — no org slug in URL)
const ProtectedRoute = ({ children, requiredRole, requiredPermission }) => {
  const { isAuthenticated, user, loading } = useAuth();
  // 2026-08-06 — Permission-based gate. `requiredPermission` accepts
  // either a single permission key or an array (any-of). Route
  // renders iff EVERY child of the OR-group is truthy on the user's
  // aggregated `any` map. SuperAdmin / TenantAdmin always pass by
  // role. Strict-instructors without the grant get redirected to
  // /dashboard (same UX as failed `requiredRole`). Prevents URL-bar
  // navigation bypass of the sidebar gates.
  const perms = useEffectivePermissions();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Preserve the intended destination so shared deep-links survive the
    // login round-trip. `?next=<path>` is honoured by LoginPage.
    const dest = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const nextParam = dest && dest !== '/' && !dest.startsWith('/login')
      ? `?next=${encodeURIComponent(dest)}`
      : '';
    return <Navigate to={`/login${nextParam}`} replace />;
  }

  // Role hierarchy: SuperAdmin > TenantAdmin > Instructor > Learner
  const roleHierarchy = {
    'SuperAdmin': 4,
    'TenantAdmin': 3,
    'Instructor': 2,
    'Learner': 1
  };

  if (requiredRole) {
    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
    if (userRoleLevel < requiredRoleLevel) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (requiredPermission) {
    // Admins always pass the permission gate (they get all-perms=true
    // from the endpoint). Wait for perms to load before deciding —
    // otherwise a race between the auth check and the perms fetch
    // would flash-redirect a valid instructor away from the page.
    if (perms.loading) {
      return <LoadingSpinner />;
    }
    if (!perms.isSuperAdmin && !perms.isTenantAdmin) {
      const keys = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
      const passes = keys.some((k) => perms.can(k));
      if (!passes) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Generate routes for both direct and org-prefixed paths
const generateRoutes = (prefix = '') => {
  const p = (path) => `${prefix}${path}`;
  
  return (
    <>
      {/* Public Routes */}
      <Route path={p('/login')} element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path={p('/register')} element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path={p('/self-onboard')} element={<PublicRoute><SelfOnboardPage /></PublicRoute>} />
      <Route path={p('/forgot-password')} element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path={p('/reset-password')} element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

      {/* Protected Routes */}
      <Route path={p('/dashboard')} element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path={p('/instructor/dashboard')} element={<ProtectedRoute requiredRole="Instructor"><InstructorDashboardPage /></ProtectedRoute>} />
      <Route path={p('/instructor/inbox')} element={<ProtectedRoute requiredRole="Instructor"><ReviewTasksPage /></ProtectedRoute>} />
      <Route path={p('/settings')} element={<ProtectedRoute requiredRole="TenantAdmin"><OrganizationSettingsPage /></ProtectedRoute>} />
      <Route path={p('/users')} element={<ProtectedRoute requiredRole="Instructor" requiredPermission="cohort.manage"><UserManagementPage /></ProtectedRoute>} />
      {/* PRD Cohort-First v2.1 / S1 — cohort drill-in route. */}
      <Route path={p('/users/cohorts/:cohortId')} element={<ProtectedRoute requiredRole="Instructor" requiredPermission="cohort.manage"><CohortDetailPage /></ProtectedRoute>} />
      <Route path={p('/courses')} element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/edit')} element={<ProtectedRoute requiredPermission={["content.edit", "content.publish"]}><CourseBuilderRoute /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId')} element={<ProtectedRoute><CourseViewRoute /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/content/:blockId')} element={<ProtectedRoute><CourseContentPage /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/content')} element={<ProtectedRoute><CourseContentPage /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/community')} element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/community/:discussionId')} element={<ProtectedRoute><DiscussionDetailPage /></ProtectedRoute>} />
      <Route path={p('/community')} element={<ProtectedRoute><CommunityLandingPage /></ProtectedRoute>} />
      <Route path={p('/analytics')} element={<ProtectedRoute requiredPermission="analytics.view"><EnhancedAnalyticsPage /></ProtectedRoute>} />
      {/* PRD: Course Merger -- Unified course creation wizard */}
      <Route path={p('/courses/new')} element={<ProtectedRoute requiredPermission={["content.edit", "content.publish"]}><CourseAuthoringWizard /></ProtectedRoute>} />
      <Route path={p('/courses/new/:courseId')} element={<ProtectedRoute requiredPermission={["content.edit", "content.publish"]}><CourseAuthoringWizard /></ProtectedRoute>} />
      <Route path={p('/courses/wizard/new')} element={<ProtectedRoute requiredRole="Instructor"><ContentWizardPage /></ProtectedRoute>} />
      <Route path={p('/courses/wizard/:courseId')} element={<ProtectedRoute requiredRole="Instructor"><ContentWizardPage /></ProtectedRoute>} />
      <Route path={p('/labs')} element={<ProtectedRoute requiredPermission={["content.edit", "content.publish"]}><LabsPage /></ProtectedRoute>} />
      {/* Industry Labs v5: /industry-labs is the Library (all roles), /play runs the
          standalone runner, /registry is the SuperAdmin bundle registry. Remaining
          legacy IL deep links fall through to the archived placeholder. */}
      <Route path={p('/industry-labs')} element={<ProtectedRoute><IndustryLabsLibraryPage /></ProtectedRoute>} />
      <Route path={p('/industry-labs/play/:blockId')} element={<ProtectedRoute><IndustryLabPlayPage /></ProtectedRoute>} />
      <Route path={p('/industry-labs/registry')} element={<ProtectedRoute requiredRole="SuperAdmin"><IndustryLabRegistryPage /></ProtectedRoute>} />
      <Route path={p('/industry-labs/lens1-gallery')} element={<ProtectedRoute requiredRole="SuperAdmin"><Lens1GalleryPage /></ProtectedRoute>} />
      <Route path={p('/il-worksheet-gallery')} element={<WorksheetGalleryPage />} />
      <Route path={p('/industry-labs/*')} element={<ProtectedRoute><IndustryLabsArchivedPage /></ProtectedRoute>} />
      <Route path={p('/industry-lab/*')} element={<ProtectedRoute><IndustryLabsArchivedPage /></ProtectedRoute>} />
      <Route path={p('/industry-lab-configurator/*')} element={<ProtectedRoute><IndustryLabsArchivedPage /></ProtectedRoute>} />
      <Route path={p('/embed/industry-labs/*')} element={<ProtectedRoute><EmbedShell><IndustryLabsArchivedPage /></EmbedShell></ProtectedRoute>} />
      <Route path={p('/embed/orgs/:orgId/labs/:labId/builder')} element={<ProtectedRoute><EmbedShell><LabBuilderPage /></EmbedShell></ProtectedRoute>} />
      <Route path={p('/help/:articleId')} element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path={p('/help')} element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path={p('/docs')} element={<ProtectedRoute requiredRole="SuperAdmin"><DocumentationPage /></ProtectedRoute>} />
      <Route path={p('/notifications')} element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      {/* 2026-02-24 — User-scoped background jobs history. Reachable
          from the GlobalJobsPill "View all jobs history →" footer
          and (optionally) from the MainLayout sidebar. */}
      <Route path={p('/jobs')} element={<ProtectedRoute requiredRole="TenantAdmin"><JobsHistoryPage /></ProtectedRoute>} />
      <Route path={p('/settings/notifications')} element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
      <Route path={p('/my-certificates')} element={<ProtectedRoute><MyCertificatesPage /></ProtectedRoute>} />
      <Route path={p('/my-analytics')} element={<ProtectedRoute><MyAnalyticsPage /></ProtectedRoute>} />
      <Route path={p('/achievements')} element={<Navigate to={p('/my-analytics')} replace />} />
      <Route path={p('/gamification-admin')} element={<ProtectedRoute requiredRole="TenantAdmin"><GamificationAdminPage /></ProtectedRoute>} />
      <Route path={p('/admin')} element={<ProtectedRoute requiredRole="TenantAdmin"><AdminConsolePage /></ProtectedRoute>} />
      <Route path={p('/support/new')} element={<ProtectedRoute><CreateTicketPage /></ProtectedRoute>} />
      <Route path={p('/support/:ticketId')} element={<ProtectedRoute><SupportTicketPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/labs/:labId/builder')} element={<ProtectedRoute requiredPermission={["content.edit", "content.publish"]}><LabBuilderPage /></ProtectedRoute>} />
      {/* W8.4: the deprecated standalone /instructor/labs/:labId/experience-studio
          redirect route was retired — the studio lives inside /builder?tab=experience. */}
      {/* Phase-1 Lab Experience Layer — learner-facing entry to the same builder page
          with the Experience overlays (Brief, Stage Rail, Reflection, Completion) wired in.
          Same component, different auth gate: learners get the pedagogical shell instead of the instructor UI. */}
      <Route path={p('/orgs/:orgId/labs/:labId/learn')} element={<ProtectedRoute><LabBuilderPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/guard-digest')} element={<ProtectedRoute><GuardDigestPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/misconceptions')} element={<ProtectedRoute><MisconceptionDashboardPage /></ProtectedRoute>} />
      {/* W8.4: /orgs/:orgId/prediction-pilot retired with the migrated pilots.
          The /prediction-pilot/* backend endpoints (incl. /eligibility) remain. */}
      <Route path={p('/orgs/:orgId/labs/:labId/results')} element={<ProtectedRoute><LabResultsPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/labs/:labId/epm-results')} element={<ProtectedRoute><EPMResultsPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/labs/:labId/submissions')} element={<ProtectedRoute><LabSubmissionsPage /></ProtectedRoute>} />
      <Route path={p('/courses/:courseId/quiz-submissions')} element={<ProtectedRoute><QuizSubmissionsPage /></ProtectedRoute>} />
      <Route path={p('/orgs/:orgId/settings')} element={<ProtectedRoute requiredRole="TenantAdmin"><OrganizationSettingsPage /></ProtectedRoute>} />
      <Route path={p('/superadmin')} element={<ProtectedRoute requiredRole="SuperAdmin"><SuperAdminDashboard /></ProtectedRoute>} />
      <Route path={p('/superadmin/video-pipeline-settings')} element={<ProtectedRoute requiredRole="SuperAdmin"><VideoPipelineSettingsPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/ai-api-calls')} element={<ProtectedRoute requiredRole="SuperAdmin"><AIApiCallsPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/lean-canvas-ai')} element={<ProtectedRoute requiredRole="SuperAdmin"><SuperAdminLeanCanvasAIPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/stakeholder-sim')} element={<ProtectedRoute requiredRole="SuperAdmin"><SuperAdminStakeholderSimPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/industry-labs')} element={<ProtectedRoute requiredRole="SuperAdmin"><IndustryLabRegistryPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/diagnostic')} element={<ProtectedRoute requiredRole="SuperAdmin"><DiagnosticAdminPage /></ProtectedRoute>} />
      {/* AI Diagnostic: invite landing is public; everything else needs a login */}
      <Route path={p('/diagnostic/invite/:token')} element={<DiagnosticInvitePage />} />
      <Route path={p('/diagnostic')} element={<ProtectedRoute><DiagnosticHomePage /></ProtectedRoute>} />
      <Route path={p('/diagnostic/s/:stageKey/*')} element={<ProtectedRoute><DiagnosticStagePage /></ProtectedRoute>} />
      <Route path={p('/diagnostic/report/:reportType')} element={<ProtectedRoute><DiagnosticReportPage /></ProtectedRoute>} />
      {/* Tier 2 Announcements */}
      <Route path={p('/admin/announcements')} element={<ProtectedRoute requiredRole="TenantAdmin"><AnnouncementComposerPage /></ProtectedRoute>} />
      <Route path={p('/admin/review-coverage')} element={<ProtectedRoute requiredRole="TenantAdmin"><ReviewCoveragePage /></ProtectedRoute>} />
      <Route path={p('/admin/announcements/history')} element={<ProtectedRoute requiredRole="Instructor"><AnnouncementHistoryPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/announcements')} element={<ProtectedRoute requiredRole="SuperAdmin"><SystemAnnouncementPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/jobs')} element={<ProtectedRoute requiredRole="SuperAdmin"><JobMonitorPage /></ProtectedRoute>} />
      <Route path={p('/superadmin/perf')} element={<ProtectedRoute requiredRole="SuperAdmin"><PerfDashboardPage /></ProtectedRoute>} />
    </>
  );
};

function AppRoutes() {
  // Single-domain mode: routes are mounted only at the root; legacy
  // /{slug}/... bookmarks are caught by <SlugRedirect /> as a fallback.
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* OAuth Callback Routes - Top level */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/callback/microsoft" element={<AuthCallback />} />

        {/* All app routes, mounted at the root */}
        {generateRoutes('')}

        {/* Redirect root to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Catch-all: strip legacy /{slug}/... bookmarks, else /dashboard. */}
        <Route path="*" element={<SlugRedirect />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <OrgProvider>
          <NotificationProvider>
            <AIGenerationProvider>
              <JobsProvider>
                <TourProvider>
                  <div className="min-h-screen bg-gray-50">
                    <CrashGuard>
                    <AppRoutes />
                    </CrashGuard>
                    <SonnerToaster position="top-right" richColors closeButton />
                    <GamificationToastContainer />
                    <SurpriseRewardContainer />
                    {/* Linear-style floating pill — auto-hides when
                        no jobs are running. Sourced from JobsContext
                        so navbar widget + per-block pill + this pill
                        all stay in sync from one network poll. */}
                    <GlobalJobsPill />
                    {/* Modal queue for stage / batch / failure events
                        that need user attention (Feb 2026). */}
                    <ActionDialogQueue />
                    {/* Passive Sonner toasts for info-only completion
                        events (Stage 3 auto-save, single block ready). */}
                    <GenerationToastBridge />
                  </div>
                </TourProvider>
              </JobsProvider>
            </AIGenerationProvider>
          </NotificationProvider>
        </OrgProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
